import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";

const WALLS = [
  {
    key: "tinana",
    title: "Tinana",
    sub: "the body",
    prompt: "How is your body today? Sleep, food, energy, the bones of you.",
    color: "#9C6644",
  },
  {
    key: "hinengaro",
    title: "Hinengaro",
    sub: "the mind",
    prompt: "How are your thoughts moving? Heavy, fast, quiet, scrambled?",
    color: "#2B4522",
  },
  {
    key: "wairua",
    title: "Wairua",
    sub: "the spirit",
    prompt: "How is your wairua sitting? Steady, dim, restless, anchored?",
    color: "#8B3A33",
  },
  {
    key: "whanau",
    title: "Whānau",
    sub: "your people",
    prompt: "How connected do you feel today — to the people who matter?",
    color: "#6B705C",
  },
];

export default function CheckIn() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [vals, setVals] = useState({ tinana: 5, hinengaro: 5, wairua: 5, whanau: 5 });
  const [extra, setExtra] = useState({ sleep_hours: "", cravings: "", mood_word: "", journal: "" });
  const [loading, setLoading] = useState(false);

  const total = WALLS.length + 1;

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/checkins", {
        ...vals,
        sleep_hours: extra.sleep_hours ? Number(extra.sleep_hours) : null,
        cravings: extra.cravings !== "" ? Number(extra.cravings) : null,
        mood_word: extra.mood_word || null,
        journal: extra.journal || null,
      });
      toast.success("Tirohia · noted. Ka pai.");
      nav("/dashboard");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto animate-fadeup">
      <div className="flex gap-1 mb-6">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-all ${i <= step ? "bg-ngahere" : "bg-border"}`}
          />
        ))}
      </div>

      {step < WALLS.length ? (
        <WallCard
          wall={WALLS[step]}
          value={vals[WALLS[step].key]}
          onChange={(v) => setVals((p) => ({ ...p, [WALLS[step].key]: v }))}
          onBack={step > 0 ? () => setStep(step - 1) : null}
          onNext={() => setStep(step + 1)}
        />
      ) : (
        <div className="bg-muka rounded-3xl p-7 sm:p-9 border border-border">
          <div className="text-[11px] uppercase tracking-[0.22em] text-kokowai mb-3">He āpiti · also</div>
          <h2 className="font-serif text-3xl text-charcoal mb-6">A few extras (skip any)</h2>
          <div className="space-y-5">
            <SmallField
              label="Sleep last night (hours)"
              value={extra.sleep_hours}
              onChange={(v) => setExtra({ ...extra, sleep_hours: v })}
              type="number"
              testid="checkin-sleep"
              placeholder="e.g. 6.5"
            />
            <SliderField
              label="Cravings or urges (0–10)"
              value={extra.cravings === "" ? 0 : Number(extra.cravings)}
              onChange={(v) => setExtra({ ...extra, cravings: v })}
              testid="checkin-cravings"
            />
            <SmallField
              label="One word for today"
              value={extra.mood_word}
              onChange={(v) => setExtra({ ...extra, mood_word: v })}
              testid="checkin-mood"
              placeholder="e.g. heavy, steady, soft"
            />
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Anything you want to put down
              </span>
              <textarea
                rows={4}
                value={extra.journal}
                onChange={(e) => setExtra({ ...extra, journal: e.target.value })}
                data-testid="checkin-journal"
                className="mt-2 w-full px-4 py-3 rounded-xl border border-border bg-bone focus:outline-none focus:ring-2 focus:ring-ngahere"
              />
            </label>
          </div>
          <div className="flex justify-between mt-8">
            <button
              onClick={() => setStep(step - 1)}
              data-testid="checkin-back-extras"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-charcoal/70 hover:bg-bone"
            >
              <ArrowLeft size={16} /> back
            </button>
            <button
              onClick={submit}
              disabled={loading}
              data-testid="checkin-submit"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-ngahere text-muka hover:bg-ngahere/90 disabled:opacity-50"
            >
              <Check size={18} /> {loading ? "..." : "Tukua · save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function WallCard({ wall, value, onChange, onBack, onNext }) {
  return (
    <div className="bg-muka rounded-3xl p-7 sm:p-9 border border-border">
      <div className="text-[11px] uppercase tracking-[0.22em] mb-3" style={{ color: wall.color }}>
        Te taha {wall.title.toLowerCase()}
      </div>
      <h2 className="font-serif text-4xl sm:text-5xl text-charcoal mb-1">{wall.title}</h2>
      <div className="text-charcoal/60 italic mb-5">{wall.sub}</div>
      <p className="text-charcoal/80 leading-relaxed mb-8">{wall.prompt}</p>

      <SliderField
        label="0 = empty · 10 = strong"
        value={value}
        onChange={onChange}
        testid={`checkin-slider-${wall.key}`}
        accent={wall.color}
      />

      <div className="flex justify-between mt-10">
        {onBack ? (
          <button
            onClick={onBack}
            data-testid={`checkin-back-${wall.key}`}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-charcoal/70 hover:bg-bone"
          >
            <ArrowLeft size={16} /> back
          </button>
        ) : (
          <span />
        )}
        <button
          onClick={onNext}
          data-testid={`checkin-next-${wall.key}`}
          className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-ngahere text-muka hover:bg-ngahere/90"
        >
          Haere tonu <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}

function SliderField({ label, value, onChange, testid, accent }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{label}</span>
        <span className="font-serif text-3xl" style={{ color: accent || "#2B4522" }}>{value}</span>
      </div>
      <input
        type="range"
        min="0"
        max="10"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className="w-full h-2 bg-border rounded-full appearance-none cursor-pointer accent-ngahere"
        style={{ accentColor: accent || "#2B4522" }}
      />
      <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
        <span>0</span><span>5</span><span>10</span>
      </div>
    </div>
  );
}

function SmallField({ label, value, onChange, testid, type = "text", placeholder }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-2 w-full px-4 py-3 rounded-xl border border-border bg-bone focus:outline-none focus:ring-2 focus:ring-ngahere"
      />
    </label>
  );
}
